/**
 * /knowledge/$knowledgeId — leitura do artigo: breadcrumb, voltar, ações
 * (editar / excluir / compartilhar), corpo central max-w-3xl com autor,
 * HTML sanitizado, anexos e caixa de feedback.
 */
import { useState } from 'react';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Copy,
  Folder,
  Globe,
  Lock,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import type { KnowledgeDetail } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { errorMessage, knowledgeDetailOptions } from '@/features/knowledge/knowledge-queries';
import {
  useDeleteKnowledge,
  useKnowledgeFeedback,
  useUpdateKnowledge,
} from '@/features/knowledge/knowledge-mutations';
import { formatDateTime, initialOf } from '@/features/knowledge/knowledge-format';
import {
  FeedbackBox,
  KnowledgeAttachments,
  KnowledgeHtml,
} from '@/features/knowledge/knowledge-article-view';

const routeApi = getRouteApi('/_authenticated/knowledge/$knowledgeId');

// ── Dialog de compartilhamento (Switch Público + link copiável) ─────────────
function ShareDialog({ detail, onClose }: { detail: KnowledgeDetail; onClose: () => void }) {
  const update = useUpdateKnowledge(detail.id);
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}/kb/${detail.publicSlug}`;

  const toggle = (next: boolean) =>
    // PUT completo (contrato KnowledgeBody) trocando apenas isPublic.
    update.mutate({
      folderId: detail.folderId,
      title: detail.title,
      body: detail.body,
      tags: detail.tags,
      isPublic: next,
    });

  const copy = () => {
    void navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Dialog open onClose={onClose} title="Compartilhar conhecimento">
      <div className="space-y-4">
        <label className="flex cursor-pointer items-center justify-between gap-3">
          <span>
            <span className="block text-sm font-medium">Público</span>
            <span className="block text-[13px] text-muted-foreground">
              Gera link compartilhável sem login
            </span>
          </span>
          <Switch
            checked={detail.isPublic}
            disabled={update.isPending}
            onCheckedChange={toggle}
            aria-label="Tornar conhecimento público"
          />
        </label>

        {detail.isPublic ? (
          <div className="space-y-1.5">
            <Label htmlFor="share-link">Link público</Label>
            <div className="flex items-center gap-2">
              <Input id="share-link" readOnly value={publicUrl} className="h-8 flex-1 text-[13px]" />
              <Button type="button" variant="outline" size="sm" onClick={copy}>
                {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          </div>
        ) : null}

        {update.error ? (
          <p className="text-sm text-destructive">{errorMessage(update.error)}</p>
        ) : null}

        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export function KnowledgeArticlePage() {
  const { knowledgeId } = routeApi.useParams();
  const id = Number(knowledgeId);
  const navigate = useNavigate();
  const detail = useQuery(knowledgeDetailOptions(id));

  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const remove = useDeleteKnowledge();
  const feedback = useKnowledgeFeedback(id);

  if (detail.isPending) {
    return (
      <div aria-busy className="mx-auto w-full max-w-3xl space-y-4">
        <Skeleton className="h-3.5 w-72" />
        <Skeleton className="h-8 w-2/3" />
        <div className="flex items-center gap-3">
          <Skeleton className="size-9 rounded-full" />
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="rounded-lg border bg-card shadow-xs">
        <EmptyState message={`Erro ao carregar conhecimento: ${errorMessage(detail.error)}`} />
      </div>
    );
  }
  const article = detail.data;

  const confirmDelete = () =>
    remove.mutate(article.id, {
      onSuccess: () =>
        void navigate({
          to: '/knowledge/folder/$folderId',
          params: { folderId: String(article.folderId) },
        }),
    });

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4">
      {/* Breadcrumb */}
      <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/knowledge" className="hover:text-foreground">
          Conhecimentos
        </Link>
        <span className="mx-1.5">/</span>
        <Link
          to="/knowledge/folder/$folderId"
          params={{ folderId: String(article.folderId) }}
          className="hover:text-foreground"
        >
          {article.folderName}
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-foreground">{article.title}</span>
      </nav>

      {/* Voltar + ações */}
      <div className="flex items-center gap-2">
        <Link
          to="/knowledge/folder/$folderId"
          params={{ folderId: String(article.folderId) }}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
        >
          <ArrowLeft className="size-4" />
          Voltar
        </Link>
        <div className="ml-auto flex items-center gap-1.5">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Editar conhecimento"
            title="Editar"
            onClick={() =>
              void navigate({
                to: '/knowledge/$knowledgeId/edit',
                params: { knowledgeId: String(article.id) },
              })
            }
          >
            <Pencil className="size-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon-sm" aria-label="Mais ações">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem variant="destructive" onSelect={() => setDeleteOpen(true)}>
                <Trash2 />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            className="text-primary hover:text-primary-pressed"
            onClick={() => setShareOpen(true)}
          >
            {article.isPublic ? <Globe className="size-4" /> : <Lock className="size-4" />}
            Compartilhar
          </Button>
        </div>
      </div>

      {/* Corpo central */}
      <article className="space-y-5 rounded-lg border bg-card p-6 shadow-xs">
        <h1 className="text-2xl font-semibold">{article.title}</h1>

        {/* Linha do autor */}
        <div className="flex flex-wrap items-center gap-3">
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground"
          >
            {initialOf(article.authorName)}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold">Escrito por {article.authorName ?? '—'}</div>
            <div className="text-sm text-muted-foreground">
              Atualizado em {formatDateTime(article.updatedAt)} · Visto {article.views}{' '}
              {article.views === 1 ? 'vez' : 'vezes'}
            </div>
          </div>
          <Badge variant="outline" className="ml-auto">
            <Folder className="size-3" />
            {article.folderName}
          </Badge>
        </div>

        <KnowledgeHtml html={article.body} />

        <KnowledgeAttachments files={article.files} />

        <FeedbackBox
          myVote={article.feedback.mine}
          counts={{ yes: article.feedback.yes, no: article.feedback.no }}
          pending={feedback.isPending}
          onVote={(helpful) => feedback.mutate(helpful)}
        />
      </article>

      {shareOpen ? <ShareDialog detail={article} onClose={() => setShareOpen(false)} /> : null}

      <Dialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          remove.reset();
        }}
        title="Excluir conhecimento"
      >
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir{' '}
          <strong className="text-foreground">{article.title}</strong>? Essa ação não pode ser
          desfeita.
        </p>
        {remove.error ? (
          <p className="mt-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setDeleteOpen(false);
              remove.reset();
            }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={confirmDelete}
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
